import { FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { AuthSplitPage } from '../components/AuthSplitPage.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { API_BASE } from '../utils/apiEndpoints';
import { LIMITS } from '../utils/validation.ts';
import { consumeAuthFrom } from '../utils/authRedirect.ts';

function translateResetMessage(t: (key: string) => string, msg: string | undefined): string {
  if (!msg) return t('resetPassword.serverError');
  const map: Record<string, string> = {
    'resetPassword.invalidToken': t('resetPassword.invalidToken'),
    'resetPassword.serverError': t('resetPassword.serverError'),
    'settings.passwordNewRequired': t('resetPassword.passwordRequired'),
    'settings.passwordLength': t('resetPassword.passwordLength'),
    'settings.passwordMismatch': t('resetPassword.passwordMismatch'),
  };
  return map[msg] || msg;
}

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(!token ? t('resetPassword.invalidToken') : '');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) {
      setError(t('resetPassword.invalidToken'));
      return;
    }
    if (password.length < LIMITS.PASSWORD_MIN || password.length > LIMITS.PASSWORD_MAX) {
      setError(t('resetPassword.passwordLength'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('resetPassword.passwordMismatch'));
      return;
    }
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/login/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, password, confirmPassword }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(translateResetMessage(t, data.message));
        return;
      }
      if (data.user) {
        login({
          id: String(data.user.id),
          username: data.user.username,
          email: data.user.email,
          avatar: data.user.avatar,
          role: data.user.role || 'user',
        });
      }
      navigate(consumeAuthFrom(), { replace: true });
    } catch {
      setError(t('resetPassword.serverError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSplitPage title={t('resetPassword.resetTitle')} lead={t('resetPassword.resetLead')}>
      {error ? <div className="login-card__error">{error}</div> : null}

      <form onSubmit={handleSubmit} className="login-form" noValidate>
        <div className="login-field">
          <label className="login-field__label" htmlFor="reset-password">
            {t('resetPassword.newPassword')}
          </label>
          <div className="login-field__control">
            <input
              id="reset-password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={LIMITS.PASSWORD_MIN}
              className="login-field__input login-field__input--with-toggle"
              autoComplete="new-password"
            />
            <button
              type="button"
              className="login-field__toggle"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
        </div>
        <div className="login-field">
          <label className="login-field__label" htmlFor="reset-confirm">
            {t('resetPassword.confirmPassword')}
          </label>
          <div className="login-field__control">
            <input
              id="reset-confirm"
              type={showPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={LIMITS.PASSWORD_MIN}
              className="login-field__input"
              autoComplete="new-password"
            />
          </div>
        </div>
        <div className="login-submit-wrap">
          <button type="submit" className="login-submit" disabled={loading || !token}>
            <span className="login-submit__label">
              {loading ? t('resetPassword.saving') : t('resetPassword.submit')}
            </span>
          </button>
        </div>
        <p className="login-card__register">
          <Link to="/login" className="login-card__register-link">
            {t('resetPassword.backToLogin')}
          </Link>
        </p>
      </form>
    </AuthSplitPage>
  );
}
